import {
  EndOwnershipReason,
  OwnershipSaleBuyerOutcome,
  TireHotelRehomeSellerContractOutcome,
  TireHotelRehomeSettlement,
  TireHotelSellerInboundMode,
  type OwnershipSaleApiData,
  type OwnershipTransferCarContext
} from '@navio/nsdk';
import { z } from 'zod';
import type { OwnershipStep } from './useOwnershipTransferWizard';

const option = z.object({ value: z.number(), label: z.string() });

const ownershipRecipientSchema = z.object({
  customer: option.optional().refine((value) => value != null, 'Select the receiving customer'),
  driverId: z.number().min(1, 'Select a driver')
});

export const ownerCorrectionSchema = ownershipRecipientSchema.extend({
  endReason: z
    .enum([EndOwnershipReason.NEVER_OWNED, EndOwnershipReason.SOLD])
    .optional()
    .refine((value) => value != null, 'Choose why the other owners are removed'),
  reason: z
    .string()
    .max(255)
    .refine((value) => value.trim().length > 0, 'Explain why the owner assignment is incorrect')
});
export type OwnerCorrectionValues = z.input<typeof ownerCorrectionSchema>;

const ownershipTransferFormObject = z.object({
  ...ownershipRecipientSchema.shape,
  moveOpenRecalls: z.boolean(),
  buyerAddress: option.optional(),
  sellerAddress: option.optional(),
  replacementCar: option.optional(),
  buyerOutcome: z.enum(OwnershipSaleBuyerOutcome),
  sellerTireHotelContractOutcome: z.enum(TireHotelRehomeSellerContractOutcome),
  settlement: z.enum(TireHotelRehomeSettlement),
  sellerInboundMode: z.enum(TireHotelSellerInboundMode),
  send: z.boolean()
});

type HotelSettingsIssue = { path: 'buyerAddress' | 'replacementCar' | 'sellerAddress'; message: string };

/** Missing hotel settlement inputs; drives both step gating and submit validation. */
const missingHotelSettings = (
  value: z.input<typeof ownershipTransferFormObject>,
  soldCarIds: number[]
): HotelSettingsIssue[] => {
  const issues: HotelSettingsIssue[] = [];
  if (!value.buyerAddress) {
    issues.push({ path: 'buyerAddress', message: 'Select the buyer address' });
  }
  if (
    value.buyerOutcome === OwnershipSaleBuyerOutcome.TAKES_OVER &&
    value.sellerTireHotelContractOutcome === TireHotelRehomeSellerContractOutcome.KEEP
  ) {
    if (!value.replacementCar) {
      issues.push({ path: 'replacementCar', message: 'Select the seller replacement car' });
    } else if (soldCarIds.includes(value.replacementCar.value)) {
      issues.push({ path: 'replacementCar', message: 'Choose a different car than the one being sold.' });
    }
    if (value.sellerInboundMode === TireHotelSellerInboundMode.PAID_PICKUP && !value.sellerAddress) {
      issues.push({ path: 'sellerAddress', message: 'Select a pickup address' });
    }
  }
  return issues;
};

export const ownershipTransferFormSchema = (hasHotel: boolean, soldCarIds: number[]) =>
  ownershipTransferFormObject.superRefine((value, ctx) => {
    if (!hasHotel) {
      return;
    }
    for (const issue of missingHotelSettings(value, soldCarIds)) {
      ctx.addIssue({ code: 'custom', path: [issue.path], message: issue.message });
    }
  });

export type OwnershipTransferFormValues = z.input<ReturnType<typeof ownershipTransferFormSchema>>;

export const ownershipTransferDefaults = (
  settlement: TireHotelRehomeSettlement,
  buyerOutcome: OwnershipSaleBuyerOutcome = OwnershipSaleBuyerOutcome.TAKES_OVER
): OwnershipTransferFormValues => ({
  customer: undefined,
  driverId: 0,
  moveOpenRecalls: true,
  buyerAddress: undefined,
  sellerAddress: undefined,
  replacementCar: undefined,
  buyerOutcome,
  sellerTireHotelContractOutcome: TireHotelRehomeSellerContractOutcome.END,
  settlement,
  sellerInboundMode: TireHotelSellerInboundMode.NONE,
  send: settlement === TireHotelRehomeSettlement.BUYER_PAYS
});

export const buildOwnershipSaleBody = (
  values: OwnershipTransferFormValues,
  carIds: number[],
  hotel: OwnershipTransferCarContext | undefined
): OwnershipSaleApiData['body'] => {
  const takesOver = values.buyerOutcome === OwnershipSaleBuyerOutcome.TAKES_OVER;
  // A buyer who collects ends the seller's tire hotel; keeping it is a takeover choice only.
  const keepSeller = takesOver && values.sellerTireHotelContractOutcome === TireHotelRehomeSellerContractOutcome.KEEP;
  const base = { carIds, userGroupMemberId: values.driverId, moveOpenRecalls: values.moveOpenRecalls };
  if (!hotel) {
    return { ...base, tireHotel: undefined };
  }
  const [hotelId] = hotel.tireHotelIds;
  const buyerAddress = values.buyerAddress;
  const [issue] = missingHotelSettings(values, carIds);
  if (!buyerAddress || hotelId == null || hotel.tireHotelIds.length !== 1 || issue) {
    throw new Error(issue?.message ?? 'A tire hotel transfer requires one active hotel and a buyer address.');
  }
  return {
    ...base,
    tireHotel: {
      tireHotelId: hotelId,
      toAddressId: buyerAddress.value,
      storageObjectGroupIdentifier: hotel.storageObjectGroupIdentifier ?? undefined,
      buyerOutcome: values.buyerOutcome,
      sellerTireHotelContractOutcome: keepSeller
        ? TireHotelRehomeSellerContractOutcome.KEEP
        : TireHotelRehomeSellerContractOutcome.END,
      settlement: values.settlement,
      sellerInboundMode: keepSeller ? values.sellerInboundMode : TireHotelSellerInboundMode.NONE,
      sellerReplacementCarId: keepSeller ? values.replacementCar?.value : undefined,
      fromAddressId:
        keepSeller && values.sellerInboundMode === TireHotelSellerInboundMode.PAID_PICKUP
          ? values.sellerAddress?.value
          : undefined,
      send: takesOver && values.settlement === TireHotelRehomeSettlement.BUYER_PAYS && values.send
    }
  };
};

/** Why a wizard step cannot continue yet, in words for the hint under Continue; undefined when it can. */
export const ownershipStepBlocker = ({
  step,
  values,
  soldCarIds,
  fromUserGroupId,
  hasDriver
}: {
  step: OwnershipStep;
  values: z.input<typeof ownershipTransferFormObject>;
  soldCarIds: number[];
  fromUserGroupId: number;
  hasDriver: boolean;
}): string | undefined => {
  switch (step) {
    case 'customer':
      if (values.customer == null || values.customer.value === fromUserGroupId) {
        return 'Choose the receiving customer';
      }
      return hasDriver ? undefined : 'Choose a driver';
    case 'hotel':
      return missingHotelSettings(values, soldCarIds)[0]?.message;
    case 'review':
      return undefined;
  }
};
